import * as React from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

/** Tipo mínimo requerido para el combobox de servicios */
interface ServicioBasico {
  id: string
  nombre: string
  precio_estimado?: number | null
  categoria?: string | null
}

interface ServicioComboboxProps {
  servicios: ServicioBasico[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function ServicioCombobox({
  servicios,
  value,
  onValueChange,
  placeholder = 'Seleccionar servicio...',
  disabled = false,
  className,
}: ServicioComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  const selectedServicio = servicios.find((s) => s.id === value)

  const filteredServicios = React.useMemo(() => {
    if (!searchQuery) return servicios

    const query = searchQuery.toLowerCase()
    return servicios.filter((servicio) => {
      const nombre = servicio.nombre.toLowerCase()
      const categoria = servicio.categoria?.toLowerCase() ?? ''
      return nombre.includes(query) || categoria.includes(query)
    })
  }, [servicios, searchQuery])

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between h-10 font-normal', className)}
        >
          {selectedServicio ? (
            <div className="flex items-center justify-between gap-2 w-full min-w-0">
              <span className="truncate">{selectedServicio.nombre}</span>
              {selectedServicio.precio_estimado != null && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatCurrency(selectedServicio.precio_estimado)}
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nombre o categoría..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-[250px] overflow-y-auto overscroll-contain">
            <CommandEmpty>
              {searchQuery
                ? 'No se encontraron servicios.'
                : 'No hay servicios disponibles.'}
            </CommandEmpty>
            <CommandGroup>
              {filteredServicios.slice(0, 100).map((servicio) => (
                <CommandItem
                  key={servicio.id}
                  value={servicio.id}
                  onSelect={(currentValue) => {
                    onValueChange(currentValue)
                    setOpen(false)
                    setSearchQuery('')
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value === servicio.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex items-center justify-between gap-2 w-full min-w-0">
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium truncate">{servicio.nombre}</span>
                      {servicio.categoria && (
                        <span className="text-xs text-muted-foreground truncate">
                          {servicio.categoria}
                        </span>
                      )}
                    </div>
                    {servicio.precio_estimado != null && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatCurrency(servicio.precio_estimado)}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
              {filteredServicios.length > 100 && (
                <div className="p-2 text-center text-xs text-muted-foreground border-t">
                  Mostrando 100 de {filteredServicios.length} resultados. Refina tu búsqueda.
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
